import { useCallback, useEffect, useRef, useState } from "react";

import { parseDetail } from "../utils/errors";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import BlockIcon from "@mui/icons-material/Block";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CloseIcon from "@mui/icons-material/Close";
import KeyIcon from "@mui/icons-material/Key";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import useRequests from "../hooks/http";

function ApplicationAccessDrawer({
  open,
  user,
  tenantId,
  applications,
  onClose,
}: {
  open: boolean;
  user: TenantMember | null;
  tenantId: string;
  applications: Application[];
  onClose: () => void;
}) {
  const { get, post, del } = useRequests();
  const [accessIds, setAccessIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedAppId, setSelectedAppId] = useState<number | "">("");

  const loadAccess = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    const { data, status } = await get(`/admin/tenants/${tenantId}/users/${user.id}/access`);
    if (status === 200 && Array.isArray(data)) setAccessIds(data);
    setLoading(false);
  }, [user, tenantId, get]);

  useEffect(() => {
    if (open && user) {
      loadAccess();
    } else {
      setAccessIds([]);
      setSelectedAppId("");
      setError("");
    }
  }, [open, user, loadAccess]);

  const handleGrant = async () => {
    if (!user || selectedAppId === "") return;
    setError("");
    const { status, data } = await post(`/admin/tenants/${tenantId}/users/${user.id}/access`, null, {
      application_id: selectedAppId,
    });
    if (status === 204 || status === 200 || status === 201) {
      setAccessIds((prev) => [...prev, selectedAppId as number]);
      setSelectedAppId("");
    } else {
      setError(parseDetail(data?.detail, "Failed to grant access"));
    }
  };

  const handleRevoke = async (appId: number) => {
    if (!user) return;
    setError("");
    const { status, data } = await del(`/admin/tenants/${tenantId}/users/${user.id}/access/${appId}`);
    if (status === 204 || status === 200) {
      setAccessIds((prev) => prev.filter((id) => id !== appId));
    } else {
      setError(parseDetail(data?.detail, "Failed to revoke access"));
    }
  };

  const grantedApps = applications.filter((a) => accessIds.includes(a.id));
  const ungrantedApps = applications.filter((a) => !accessIds.includes(a.id));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 460, p: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          Application Access — {user?.first_name} {user?.last_name}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 3 }} />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <FormControl fullWidth size="small" disabled={ungrantedApps.length === 0}>
          <InputLabel>Grant access</InputLabel>
          <Select
            value={selectedAppId}
            label="Grant access"
            onChange={(e) => setSelectedAppId(e.target.value as number | "")}
          >
            {ungrantedApps.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleGrant}
          disabled={selectedAppId === "" || ungrantedApps.length === 0}
          sx={{ minWidth: 72 }}
        >
          Grant
        </Button>
      </Box>
      {ungrantedApps.length === 0 && grantedApps.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Access has been granted to all available applications.
        </Typography>
      )}
      {grantedApps.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No application access granted yet.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {grantedApps.map((app) => (
            <Box
              key={app.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2">{app.name}</Typography>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  {app.identifier}
                </Typography>
              </Box>
              <Button size="small" color="error" onClick={() => handleRevoke(app.id)}>Revoke</Button>
            </Box>
          ))}
        </Box>
      )}
    </Drawer>
  );
}

function AssignRolesDrawer({
  open,
  user,
  tenantId,
  roles,
  onClose,
}: {
  open: boolean;
  user: TenantMember | null;
  tenantId: string;
  roles: Role[];
  onClose: () => void;
}) {
  const { get, post, del } = useRequests();
  const [assignedRoleIds, setAssignedRoleIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState<number | "">("");

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    const { data, status } = await get(`/admin/tenants/${tenantId}/users/${user.id}/roles`);
    if (status === 200 && Array.isArray(data)) setAssignedRoleIds(data);
    setLoading(false);
  }, [user, tenantId, get]);

  useEffect(() => {
    if (open && user) {
      load();
    } else {
      setAssignedRoleIds([]);
      setSelectedRoleId("");
      setError("");
    }
  }, [open, user, load]);

  const handleAssign = async () => {
    if (!user || selectedRoleId === "") return;
    setError("");
    const { status, data } = await post(`/admin/tenants/${tenantId}/users/${user.id}/roles`, null, {
      role_id: selectedRoleId,
    });
    if (status === 204 || status === 200 || status === 201) {
      setAssignedRoleIds((prev) => [...prev, selectedRoleId as number]);
      setSelectedRoleId("");
    } else {
      setError(parseDetail(data?.detail, "Failed to assign role"));
    }
  };

  const handleRemove = async (roleId: number) => {
    if (!user) return;
    setError("");
    const { status, data } = await del(`/admin/tenants/${tenantId}/users/${user.id}/roles/${roleId}`);
    if (status === 204 || status === 200) {
      setAssignedRoleIds((prev) => prev.filter((id) => id !== roleId));
    } else {
      setError(parseDetail(data?.detail, "Failed to remove role"));
    }
  };

  const assignedRoles = roles.filter((r) => assignedRoleIds.includes(r.id));
  const unassignedRoles = roles.filter((r) => r.active && !assignedRoleIds.includes(r.id));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 460, p: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          Assign Roles — {user?.first_name} {user?.last_name}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider sx={{ mb: 3 }} />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Assign role</InputLabel>
          <Select
            value={selectedRoleId}
            label="Assign role"
            onChange={(e) => setSelectedRoleId(e.target.value as number | "")}
          >
            {unassignedRoles.map((r) => (
              <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleAssign} disabled={selectedRoleId === ""} sx={{ minWidth: 80 }}>
          Assign
        </Button>
      </Box>
      {assignedRoles.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No roles assigned.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {assignedRoles.map((r) => (
            <Chip key={r.id} label={r.name} color="primary" variant="outlined" onDelete={() => handleRemove(r.id)} />
          ))}
        </Box>
      )}
    </Drawer>
  );
}

function AssignPermissionsDrawer({
  open,
  user,
  tenantId,
  permissions,
  applications,
  onClose,
}: {
  open: boolean;
  user: TenantMember | null;
  tenantId: string;
  permissions: Permission[];
  applications: Application[];
  onClose: () => void;
}) {
  const { get, post, del } = useRequests();
  const [assignedPermissionIds, setAssignedPermissionIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedAppId, setSelectedAppId] = useState<number | "">("");
  const [selectedPermissionId, setSelectedPermissionId] = useState<number | "">("");

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    const { data, status } = await get(`/admin/tenants/${tenantId}/users/${user.id}/permissions`);
    if (status === 200 && Array.isArray(data)) setAssignedPermissionIds(data);
    setLoading(false);
  }, [user, tenantId, get]);

  useEffect(() => {
    if (open && user) {
      load();
    } else {
      setAssignedPermissionIds([]);
      setSelectedPermissionId("");
      setSelectedAppId("");
      setError("");
    }
  }, [open, user, load]);

  const handleAssign = async () => {
    if (!user || selectedPermissionId === "") return;
    setError("");
    const { status, data } = await post(
      `/admin/tenants/${tenantId}/users/${user.id}/permissions`,
      null,
      { permission_id: selectedPermissionId },
    );
    if (status === 204 || status === 200 || status === 201) {
      setAssignedPermissionIds((prev) => [...prev, selectedPermissionId as number]);
      setSelectedPermissionId("");
    } else {
      setError(parseDetail(data?.detail, "Failed to assign permission"));
    }
  };

  const handleRemove = async (permissionId: number) => {
    if (!user) return;
    setError("");
    const { status, data } = await del(
      `/admin/tenants/${tenantId}/users/${user.id}/permissions/${permissionId}`,
    );
    if (status === 204 || status === 200) {
      setAssignedPermissionIds((prev) => prev.filter((id) => id !== permissionId));
    } else {
      setError(parseDetail(data?.detail, "Failed to remove permission"));
    }
  };

  const appPermissions =
    selectedAppId !== ""
      ? permissions.filter((p) => p.application_id === selectedAppId)
      : [];

  const assignedPermissions = permissions.filter((p) => assignedPermissionIds.includes(p.id));
  const unassignedInApp = appPermissions.filter((p) => !assignedPermissionIds.includes(p.id));

  const appForPermission = (p: Permission) =>
    applications.find((a) => a.id === p.application_id);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 460, p: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          Assign Permissions — {user?.first_name} {user?.last_name}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Direct permissions are assigned independently of roles and always appear in the JWT.
      </Typography>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Application</InputLabel>
        <Select
          value={selectedAppId}
          label="Application"
          onChange={(e) => {
            setSelectedAppId(e.target.value as number | "");
            setSelectedPermissionId("");
          }}
        >
          {applications.map((a) => (
            <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <FormControl fullWidth size="small" disabled={selectedAppId === ""}>
          <InputLabel>Permission</InputLabel>
          <Select
            value={selectedPermissionId}
            label="Permission"
            onChange={(e) => setSelectedPermissionId(e.target.value as number | "")}
          >
            {unassignedInApp.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.value}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={selectedPermissionId === ""}
          sx={{ minWidth: 80 }}
        >
          Assign
        </Button>
      </Box>
      {assignedPermissions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No direct permissions assigned.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {assignedPermissions.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2" fontFamily="monospace">{p.value}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {appForPermission(p)?.name ?? "Unknown app"}
                </Typography>
              </Box>
              <Button size="small" color="error" onClick={() => handleRemove(p.id)}>Remove</Button>
            </Box>
          ))}
        </Box>
      )}
    </Drawer>
  );
}

function UserActionMenu({
  user,
  onAssignRoles,
  onAssignPermissions,
  onApplicationAccess,
  onDeactivate,
}: {
  user: TenantMember;
  onAssignRoles: () => void;
  onAssignPermissions: () => void;
  onApplicationAccess: () => void;
  onDeactivate: () => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const handle = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <>
      <IconButton size="small" ref={anchorRef} onClick={() => setOpen(true)}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorRef.current} open={open} onClose={() => setOpen(false)}>
        <MenuItem onClick={() => handle(onAssignRoles)}>
          <ListItemIcon><AssignmentIndIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Assign Roles</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handle(onAssignPermissions)}>
          <ListItemIcon><ChecklistIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Assign Permissions</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handle(onApplicationAccess)}>
          <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Application Access</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handle(onDeactivate)} sx={{ color: "error.main" }}>
          <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Deactivate</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default function UserManagement() {
  const { t } = useTranslation();
  const { id: tenantId } = useParams<{ id: string }>();
  const { get, post, del } = useRequests();

  const [users, setUsers] = useState<TenantMember[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [userRoleCounts, setUserRoleCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({
    email: "",
    username: "",
    first_name: "",
    last_name: "",
    role: "member",
  });

  const [rolesDrawerUser, setRolesDrawerUser] = useState<TenantMember | null>(null);
  const [permissionsDrawerUser, setPermissionsDrawerUser] = useState<TenantMember | null>(null);
  const [accessDrawerUser, setAccessDrawerUser] = useState<TenantMember | null>(null);

  const refreshRoleCount = useCallback(
    async (userId: number) => {
      const { data, status } = await get(`/admin/tenants/${tenantId}/users/${userId}/roles`);
      if (status === 200 && Array.isArray(data)) {
        setUserRoleCounts((prev) => ({ ...prev, [userId]: data.length }));
      }
    },
    [tenantId, get],
  );

  useEffect(() => {
    Promise.all([
      get(`/admin/tenants/${tenantId}/users`),
      get(`/admin/tenants/${tenantId}/roles`),
      get(`/admin/tenants/${tenantId}/applications`),
      get(`/admin/tenants/${tenantId}/permissions`),
    ]).then(async ([usersRes, rolesRes, appsRes, permsRes]) => {
      if (usersRes.status === 200 && Array.isArray(usersRes.data)) {
        setUsers(usersRes.data);
        const counts = await Promise.all(
          (usersRes.data as TenantMember[]).map(async (u) => {
            const { data, status } = await get(
              `/admin/tenants/${tenantId}/users/${u.id}/roles`,
            );
            return [u.id, status === 200 && Array.isArray(data) ? data.length : 0] as const;
          }),
        );
        setUserRoleCounts(Object.fromEntries(counts));
      } else if (usersRes.status !== 200) {
        setError(parseDetail(usersRes.data?.detail, "Failed to load users"));
      }
      if (rolesRes.status === 200 && Array.isArray(rolesRes.data)) setRoles(rolesRes.data);
      if (appsRes.status === 200 && Array.isArray(appsRes.data)) setApplications(appsRes.data);
      if (permsRes.status === 200 && Array.isArray(permsRes.data)) setPermissions(permsRes.data);
      setLoading(false);
    });
  }, [tenantId, get]);

  const handleInvite = async () => {
    setError("");
    const { data, status } = await post(`/admin/tenants/${tenantId}/users`, null, {
      email: invite.email,
      username: invite.username,
      first_name: invite.first_name,
      last_name: invite.last_name,
      role: invite.role,
    });
    if (status === 201 || status === 200) {
      setUsers((prev) => [...prev, data]);
      setInviteOpen(false);
      setInvite({ email: "", username: "", first_name: "", last_name: "", role: "member" });
    } else {
      setError(parseDetail(data?.detail, "Failed to invite user"));
    }
  };

  const handleDeactivate = async (userId: number) => {
    const { status } = await del(`/admin/tenants/${tenantId}/users/${userId}`);
    if (status === 200 || status === 204) {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, active: false } : u)));
    }
  };

  if (loading) return <LinearProgress />;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h4">{t("userManagement.title")}</Typography>
        <Button variant="contained" onClick={() => setInviteOpen(true)}>
          Invite User
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("userManagement.description")}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.first_name} {user.last_name}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email || "-"}</TableCell>
                <TableCell>
                  {(userRoleCounts[user.id] ?? 0) > 0 ? (
                    <Chip
                      size="small"
                      label={`${userRoleCounts[user.id]} ${userRoleCounts[user.id] === 1 ? "role" : "roles"}`}
                      variant="outlined"
                      color="primary"
                      onClick={() => setRolesDrawerUser(user)}
                      sx={{ cursor: "pointer" }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      sx={{ cursor: "pointer" }}
                      onClick={() => setRolesDrawerUser(user)}
                    >
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={user.active ? "Active" : "Inactive"}
                    color={user.active ? "success" : "default"}
                  />
                </TableCell>
                <TableCell>{new Date(user.created).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  {user.active && (
                    <UserActionMenu
                      user={user}
                      onAssignRoles={() => setRolesDrawerUser(user)}
                      onAssignPermissions={() => setPermissionsDrawerUser(user)}
                      onApplicationAccess={() => setAccessDrawerUser(user)}
                      onDeactivate={() => handleDeactivate(user.id)}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {users.length === 0 && (
        <Typography color="text.secondary" sx={{ p: 2 }}>No members in this tenant.</Typography>
      )}

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            type="email"
            value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
          />
          <TextField
            label="Username"
            fullWidth
            margin="normal"
            value={invite.username}
            onChange={(e) => setInvite({ ...invite, username: e.target.value })}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="First Name"
              fullWidth
              margin="normal"
              value={invite.first_name}
              onChange={(e) => setInvite({ ...invite, first_name: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              margin="normal"
              value={invite.last_name}
              onChange={(e) => setInvite({ ...invite, last_name: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInvite} disabled={!invite.email || !invite.username}>
            Invite
          </Button>
        </DialogActions>
      </Dialog>

      <AssignRolesDrawer
        open={Boolean(rolesDrawerUser)}
        user={rolesDrawerUser}
        tenantId={tenantId ?? ""}
        roles={roles}
        onClose={() => {
          if (rolesDrawerUser) refreshRoleCount(rolesDrawerUser.id);
          setRolesDrawerUser(null);
        }}
      />

      <AssignPermissionsDrawer
        open={Boolean(permissionsDrawerUser)}
        user={permissionsDrawerUser}
        tenantId={tenantId ?? ""}
        permissions={permissions}
        applications={applications}
        onClose={() => setPermissionsDrawerUser(null)}
      />

      <ApplicationAccessDrawer
        open={Boolean(accessDrawerUser)}
        user={accessDrawerUser}
        tenantId={tenantId ?? ""}
        applications={applications}
        onClose={() => setAccessDrawerUser(null)}
      />
    </Box>
  );
}
